import { createSelector } from 'reselect'
import get from 'lodash/get'
import {
  typographySelector,
  getColorPrimarySelector,
  getConstantSelector,
  getSignalFontVariantName,
  getSpacingSelector
} from '@signal/theme'
import { darken, lighten } from '@material-ui/core/styles/colorManipulator'
import { COLOR_PRIMARY_DARK } from '@signal/theme/variables'

const getFontVariantSelector = variant =>
  createSelector(typographySelector, typography =>
    get(typography, getSignalFontVariantName(variant))
  )
const body4VariantSelector = getFontVariantSelector('body4')
const body1VariantSelector = getFontVariantSelector('body1')

const colorPrimarySelector = getColorPrimarySelector()

const buttonMinWidthSelector = getConstantSelector('BUTTON_MIN_WIDTH')
const buttonMinHeightSelector = getConstantSelector('BUTTON_MIN_HEIGHT')
const buttonSmallMinWidthSelector = getConstantSelector(
  'BUTTON_SMALL_MIN_WIDTH'
)
const buttonSmallMinHeightSelector = getConstantSelector(
  'BUTTON_SMALL_MIN_HEIGHT'
)
const buttonLargeMinWidthSelector = getConstantSelector(
  'BUTTON_LARGE_MIN_WIDTH'
)
const buttonLargeMinHeightSelector = getConstantSelector(
  'BUTTON_LARGE_MIN_HEIGHT'
)

const horizontalSpacingSelector = getSpacingSelector(2)
const verticalSpacingSelector = getSpacingSelector()

export const NEW_DESIGN_OVERRIDES = {
  // a-k-a "primary"
  contained: {
    minWidth: '120px',
    borderRadius: '25px',
    fontSize: '19px',
    padding: '16px 20px',
    boxShadow: 'none',
    transition: 'all 0.2s',
    '&$focusVisible': {
      boxShadow: 'none'
    },
    '&:active': {
      boxShadow: 'none'
    },
    '&$disabled': {
      boxShadow: 'none'
    },
    '&:hover': {
      boxShadow:
        '0px 2px 0px -1px rgba(0,0,0,0.09), 0px 4px 5px 0px rgba(0,0,0,0.08), 0px 1px 10px 0px rgba(0,0,0,0.08)',
      transform: 'scale(1.03)'
    }
  },
  // a-k-a "secondary"
  outlined: {
    minWidth: '120px',

    borderWidth: '1px',
    borderRadius: '25px',
    fontSize: '19px',
    padding: '16px 20px',
    boxShadow: 'none',
    '&$focusVisible': {
      boxShadow: 'none'
    },
    '&:active': {
      boxShadow: 'none'
    },
    '&:hover': {
      // borderColor: '#5dbfe9'
      borderColor: COLOR_PRIMARY_DARK
    },
    '&$disabled': {
      borderRadius: '25px',
      boxShadow: 'none'
    }
  },
  text: {
    minWidth: '120px',
    borderRadius: '25px',
    fontSize: '19px',
    padding: '16px 20px',
    boxShadow: 'none',
    '&$focusVisible': {
      boxShadow: 'none'
    },
    '&:active': {
      boxShadow: 'none'
    },
    '&$disabled': {
      boxShadow: 'none'
    }
  },
  sizeSmall: {
    padding: '0px 11px !important',
    minHeight: '30px !important',
    minWidth: '60px !important',
    fontSize: '14px !important'
  }
}

export default (ruleOverrides = () => {}) =>
  theme => ({
    root: {
      textTransform: 'none',
      minWidth: buttonMinWidthSelector(theme),
      minHeight: buttonMinHeightSelector(theme),
      padding: `${verticalSpacingSelector(theme)}px ${horizontalSpacingSelector(
        theme
      )}px`,
      ...body4VariantSelector(theme)
    },
    sizeSmall: {
      minWidth: buttonSmallMinWidthSelector(theme),
      minHeight: buttonSmallMinHeightSelector(theme)
    },
    sizeLarge: {
      minWidth: buttonLargeMinWidthSelector(theme),
      minHeight: buttonLargeMinHeightSelector(theme),
      ...body1VariantSelector(theme)
    },
    contained: {
      boxShadow: 'none',
      '&$focusVisible': {
        boxShadow: 'none'
      },
      '&:active': {
        boxShadow: 'none'
      },
      '&$disabled': {
        boxShadow: 'none'
      }
    },
    containedPrimary: {
      backgroundColor: colorPrimarySelector(theme),
      '&:hover': {
        backgroundColor: darken(colorPrimarySelector(theme), 0.2),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: colorPrimarySelector(theme)
        }
      }
    },
    containedSecondary: {
      backgroundColor: lighten(colorPrimarySelector(theme), 0.7),
      color: darken(colorPrimarySelector(theme), 0.2),

      '&:hover': {
        backgroundColor: lighten(colorPrimarySelector(theme), 0.6),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: lighten(colorPrimarySelector(theme), 0.7)
        }
      }
    },
    outlinedPrimary: {
      borderColor: colorPrimarySelector(theme)
    },
    outlinedSecondary: {
      borderColor: lighten(colorPrimarySelector(theme), 0.55),
      color: lighten(colorPrimarySelector(theme), 0.45)
    },
    /* Styles applied to the ButtonBase root element if the button is keyboard focused. */
    focusVisible: {},
    /* Styles applied to the root element if `disabled={true}`. */
    disabled: {},
    /* optional override or additional styles */
    ...ruleOverrides(theme)
  })
